import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    getOccurrencesClass, getOccurrencesStudent, createOccurrence, updateOccurrence, deleteOccurrence,
} from '../../Api';
import LoadingSpinner from '../../components/Loading';
import { TIPOS_OCORRENCIA, GRAVIDADES, CartaoOcorrencia } from '../../components/Ocorrencias';

const hoje = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const vazio = () => ({ data: hoje(), tipo: '', gravidade: 'LEVE', descricao: '', providencia: '', responsavelChamado: false, visivelPais: true });

const caixa = {
    width: '100%', maxWidth: 900, boxSizing: 'border-box', margin: '0 auto',
    background: 'var(--surface, #fff)', color: 'var(--text, #16323b)',
    border: '1px solid var(--line, #e3e8eb)', borderRadius: 14, padding: 20,
};
const btn = (primario, cor) => ({
    padding: '10px 18px', borderRadius: 10, fontWeight: 800, cursor: 'pointer',
    border: primario ? 'none' : `1px solid ${cor || 'var(--line, #d0d7db)'}`,
    background: primario ? (cor || 'var(--teal, #003e4f)') : 'transparent',
    color: primario ? '#fff' : (cor || 'var(--text, #16323b)'),
});
const campo = {
    width: '100%', boxSizing: 'border-box', padding: '9px 11px', borderRadius: 8,
    border: '1px solid var(--line, #d0d7db)', background: 'var(--surface, #fff)', color: 'var(--text, #16323b)', fontSize: 14,
};
const rotulo = { display: 'block', fontSize: 13, fontWeight: 700, margin: '10px 0 4px' };
const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(15, 30, 40, .45)', backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 12,
};
const modal = {
    background: 'var(--surface, #fff)', color: 'var(--text, #16323b)', borderRadius: 14, width: '100%', maxWidth: 560,
    maxHeight: '92vh', overflowY: 'auto', padding: '20px 20px 16px', boxShadow: '0 20px 50px rgba(0,0,0,.25)', boxSizing: 'border-box',
};
const CSS_IMPRESSAO = `
@media print {
  body * { visibility: hidden; }
  #ocorrencias-print, #ocorrencias-print * { visibility: visible; }
  #ocorrencias-print { position: absolute; left: 0; top: 0; width: 100%; border: none !important; }
  .nao-imprimir { display: none !important; }
  .ocorrencia-card { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  @page { size: A4 portrait; margin: 12mm; }
}`;

const Occurrences = () => {
    const navigate = useNavigate();
    const { id_class, id_student } = useParams();
    const modoTurma = !!id_class && !id_student;

    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState('');
    const [dadosTurma, setDadosTurma] = useState(null);
    const [busca, setBusca] = useState('');
    const [dados, setDados] = useState(null);
    const [ano, setAno] = useState('');
    const [filtroTipo, setFiltroTipo] = useState('');
    const [form, setForm] = useState(null); // { ...campos, id? }
    const [salvando, setSalvando] = useState(false);
    const [erroForm, setErroForm] = useState('');
    const [excluir, setExcluir] = useState(null);
    const [aviso, setAviso] = useState(null);

    const carregar = useCallback(async () => {
        setLoading(true);
        setErro('');
        try {
            if (modoTurma) {
                const r = await getOccurrencesClass(id_class);
                setDadosTurma(r.data);
            } else {
                const r = await getOccurrencesStudent(id_student, ano || undefined);
                setDados(r.data);
                if (!ano && r.data?.year) setAno(String(r.data.year));
            }
        } catch (e) {
            setErro(e?.response?.data?.msg || 'Não foi possível carregar as ocorrências.');
        } finally {
            setLoading(false);
        }
    }, [modoTurma, id_class, id_student, ano]);

    useEffect(() => { carregar(); }, [carregar]);

    const lista = useMemo(
        () => (dados?.ocorrencias || []).filter((o) => !filtroTipo || o.tipo === filtroTipo),
        [dados, filtroTipo]
    );
    const contagem = useMemo(() => {
        const c = {};
        (dados?.ocorrencias || []).forEach((o) => { c[o.tipo] = (c[o.tipo] || 0) + 1; });
        return c;
    }, [dados]);

    const salvar = async () => {
        setErroForm('');
        if (!form.tipo) return setErroForm('Escolha o tipo da ocorrência.');
        if (!form.data) return setErroForm('Informe a data.');
        if (!form.descricao.trim()) return setErroForm('Descreva a ocorrência.');
        setSalvando(true);
        try {
            if (form.id) await updateOccurrence({ ...form });
            else await createOccurrence({ ...form, id_student });
            setAviso({ tom: 'ok', texto: form.id ? 'Ocorrência atualizada.' : 'Ocorrência registrada.' });
            setForm(null);
            await carregar();
        } catch (e) {
            setErroForm(e?.response?.data?.msg || 'Erro ao salvar a ocorrência.');
        } finally {
            setSalvando(false);
        }
    };

    const confirmarExcluir = async () => {
        const o = excluir;
        setExcluir(null);
        try {
            await deleteOccurrence(o._id);
            setAviso({ tom: 'ok', texto: 'Ocorrência excluída.' });
            await carregar();
        } catch (e) {
            setAviso({ tom: 'erro', texto: e?.response?.data?.msg || 'Erro ao excluir a ocorrência.' });
        }
    };

    if (loading && !dados && !dadosTurma) return <LoadingSpinner />;

    // ───────────── modo TURMA: lista de alunos com contagem ─────────────
    if (modoTurma) {
        const alunos = (dadosTurma?.alunos || []).filter((a) => !busca || a.nome.toLowerCase().includes(busca.toLowerCase()));
        return (
            <div style={{ width: '100%', padding: 16, boxSizing: 'border-box' }}>
                <div style={caixa}>
                    <h2 style={{ margin: '0 0 4px' }}>📝 Ocorrências da turma</h2>
                    {dadosTurma?.turma && (
                        <p style={{ margin: '0 0 14px', color: 'var(--text-muted, #5b6b72)', fontSize: 14 }}>
                            {dadosTurma.turma.serie} {dadosTurma.turma.name} · {dadosTurma.turma.year}. Toque no aluno para ver ou registrar.
                        </p>
                    )}
                    {erro && <div style={{ color: '#dc2626', marginBottom: 10 }}>{erro}</div>}
                    {!erro && (
                        <>
                            <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar aluno" style={{ ...campo, marginBottom: 12 }} />
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {alunos.map((a) => (
                                    <button key={a.id_student} type="button"
                                        onClick={() => navigate(`/occurrences/student/${a.id_student}`)}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', width: '100%',
                                            padding: '12px 14px', borderRadius: 11, cursor: 'pointer',
                                            border: '1px solid var(--line, #e3e8eb)', background: 'var(--surface, #fff)', color: 'var(--text, #16323b)',
                                        }}>
                                        <span style={{ flex: 1, minWidth: 0, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.nome}</span>
                                        {a.graves > 0 && <span title="Ocorrências graves" style={{ fontSize: 12, fontWeight: 800, color: '#dc2626' }}>⚠️ {a.graves} grave{a.graves > 1 ? 's' : ''}</span>}
                                        {a.elogios > 0 && <span title="Elogios" style={{ fontSize: 12, fontWeight: 800, color: '#16a34a' }}>⭐ {a.elogios}</span>}
                                        <span style={{
                                            minWidth: 26, textAlign: 'center', padding: '2px 8px', borderRadius: 999, fontSize: 12.5, fontWeight: 800,
                                            background: a.total ? '#e8f0fe' : 'var(--line, #eef1f3)', color: a.total ? '#1d4ed8' : 'var(--text-muted, #8a979d)',
                                        }}>{a.total}</span>
                                    </button>
                                ))}
                                {alunos.length === 0 && <div style={{ color: 'var(--text-muted, #5b6b72)' }}>Nenhum aluno encontrado.</div>}
                            </div>
                        </>
                    )}
                    <div style={{ marginTop: 14 }}>
                        <button type="button" onClick={() => navigate(-1)} style={btn(false)}>Voltar</button>
                    </div>
                </div>
            </div>
        );
    }

    // ───────────── modo ALUNO ─────────────
    return (
        <div style={{ width: '100%', padding: 16, boxSizing: 'border-box' }}>
            <style>{CSS_IMPRESSAO}</style>
            <div style={caixa} id="ocorrencias-print">
                <h2 style={{ margin: '0 0 4px' }}>📝 Ocorrências — {dados?.aluno?.nome || ''}</h2>
                <p style={{ margin: '0 0 12px', color: 'var(--text-muted, #5b6b72)', fontSize: 14 }}>
                    {dados?.turma ? <>Turma {dados.turma.serie} {dados.turma.name} · </> : null}Ano letivo {ano}
                </p>

                {erro && <div style={{ color: '#dc2626', marginBottom: 10 }}>{erro}</div>}

                {!erro && (
                    <>
                        <div className="nao-imprimir" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                            {(dados?.anos || []).length > 1 && (
                                <select value={ano} onChange={(e) => setAno(e.target.value)} style={{ ...campo, width: 'auto' }} aria-label="Ano letivo">
                                    {dados.anos.map((y) => <option key={y} value={y}>{y}</option>)}
                                </select>
                            )}
                            <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)} style={{ ...campo, width: 'auto' }} aria-label="Filtrar por tipo">
                                <option value="">Todos os tipos ({(dados?.ocorrencias || []).length})</option>
                                {Object.entries(TIPOS_OCORRENCIA).map(([k, t]) => (
                                    <option key={k} value={k}>{t.icone} {t.label} ({contagem[k] || 0})</option>
                                ))}
                            </select>
                            <span style={{ flex: 1 }} />
                            {(dados?.ocorrencias || []).length > 0 && (
                                <button type="button" onClick={() => window.print()} style={btn(false)}>🖨️ Imprimir</button>
                            )}
                            {dados?.podeRegistrar && (
                                <button type="button" onClick={() => { setErroForm(''); setForm(vazio()); }} style={btn(true)}>＋ Nova ocorrência</button>
                            )}
                        </div>

                        {aviso && (
                            <div className="nao-imprimir" style={{
                                margin: '0 0 10px', padding: '9px 12px', borderRadius: 8, fontSize: 14,
                                background: aviso.tom === 'ok' ? '#e3f4ec' : '#fde8e8', color: aviso.tom === 'ok' ? '#166534' : '#991b1b',
                            }}>{aviso.texto}</div>
                        )}

                        {lista.length === 0 ? (
                            <div style={{ color: 'var(--text-muted, #5b6b72)', padding: '18px 0' }}>Nenhuma ocorrência registrada{filtroTipo ? ' deste tipo' : ''} neste ano.</div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {lista.map((o) => (
                                    <CartaoOcorrencia key={o._id} o={o}
                                        onEditar={(x) => { setErroForm(''); setForm({ ...vazio(), ...x, id: x._id }); }}
                                        onExcluir={(x) => setExcluir(x)} />
                                ))}
                            </div>
                        )}

                    </>
                )}

                <div className="nao-imprimir" style={{ marginTop: 14 }}>
                    <button type="button" onClick={() => navigate(-1)} style={btn(false)}>Voltar</button>
                </div>
            </div>

            {form && (
                <div style={overlay} onClick={() => !salvando && setForm(null)}>
                    <div style={modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                        <h3 style={{ margin: '0 0 6px' }}>{form.id ? 'Editar ocorrência' : 'Nova ocorrência'}</h3>
                        <div style={{ fontSize: 13, color: 'var(--text-muted, #5b6b72)' }}>{dados?.aluno?.nome}</div>

                        <label style={rotulo}>Tipo</label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {Object.entries(TIPOS_OCORRENCIA).map(([k, t]) => {
                                const ativo = form.tipo === k;
                                return (
                                    <button key={k} type="button" onClick={() => setForm((f) => ({ ...f, tipo: k }))} aria-pressed={ativo} style={{
                                        padding: '7px 12px', borderRadius: 999, cursor: 'pointer', fontSize: 13, fontWeight: 700,
                                        border: `2px solid ${t.cor}`, background: ativo ? t.cor : 'transparent', color: ativo ? '#fff' : t.cor,
                                    }}>{t.icone} {t.label}</button>
                                );
                            })}
                        </div>

                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            <div style={{ flex: '1 1 160px' }}>
                                <label style={rotulo}>Data</label>
                                <input type="date" value={form.data} max={hoje()} onChange={(e) => setForm((f) => ({ ...f, data: e.target.value }))} style={campo} />
                            </div>
                            {form.tipo !== 'ELOGIO' && (
                                <div style={{ flex: '1 1 200px' }}>
                                    <label style={rotulo}>Gravidade</label>
                                    <div style={{ display: 'flex', gap: 6 }}>
                                        {Object.entries(GRAVIDADES).map(([k, g]) => {
                                            const ativo = form.gravidade === k;
                                            return (
                                                <button key={k} type="button" onClick={() => setForm((f) => ({ ...f, gravidade: k }))} aria-pressed={ativo} style={{
                                                    flex: 1, padding: '8px 6px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 800,
                                                    border: `2px solid ${g.cor}`, background: ativo ? g.cor : 'transparent', color: ativo ? '#fff' : g.cor,
                                                }}>{g.label}</button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>

                        <label style={rotulo}>Descrição</label>
                        <textarea rows={4} value={form.descricao} onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))} style={{ ...campo, resize: 'vertical' }} placeholder="O que aconteceu" />

                        <label style={rotulo}>Providência tomada (opcional)</label>
                        <textarea rows={2} value={form.providencia} onChange={(e) => setForm((f) => ({ ...f, providencia: e.target.value }))} style={{ ...campo, resize: 'vertical' }} placeholder="Ex.: conversa com o aluno, bilhete na agenda…" />

                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 14, cursor: 'pointer' }}>
                            <input type="checkbox" checked={form.responsavelChamado} onChange={(e) => setForm((f) => ({ ...f, responsavelChamado: e.target.checked }))} style={{ width: 17, height: 17 }} />
                            O responsável foi chamado à escola
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 14, cursor: 'pointer' }}>
                            <input type="checkbox" checked={form.visivelPais} onChange={(e) => setForm((f) => ({ ...f, visivelPais: e.target.checked }))} style={{ width: 17, height: 17 }} />
                            Visível para os pais no portal
                        </label>
                        {!form.visivelPais && (
                            <div style={{ fontSize: 12.5, color: 'var(--text-muted, #5b6b72)', marginLeft: 25 }}>Ocorrência interna: só a escola vê.</div>
                        )}

                        {erroForm && <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 8, background: '#fde8e8', color: '#991b1b', fontSize: 14 }}>{erroForm}</div>}
                        {!form.id && (
                            <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--text-muted, #5b6b72)' }}>
                                Você pode editar ou excluir esta ocorrência até o fim do dia. Depois disso, só a direção ou a secretaria.
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 14 }}>
                            <button type="button" onClick={() => setForm(null)} disabled={salvando} style={btn(false)}>Cancelar</button>
                            <button type="button" onClick={salvar} disabled={salvando} style={btn(true)}>{salvando ? 'Salvando…' : 'Salvar'}</button>
                        </div>
                    </div>
                </div>
            )}

            {excluir && (
                <div style={overlay} onClick={() => setExcluir(null)}>
                    <div style={{ ...modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                        <h3 style={{ margin: '0 0 10px' }}>Excluir ocorrência?</h3>
                        <p style={{ margin: '0 0 16px', fontSize: 15 }}>
                            A ocorrência de {String(excluir.data).split('-').reverse().join('/')} ({TIPOS_OCORRENCIA[excluir.tipo]?.label}) será excluída. Esta ação fica registrada no histórico de movimentações.
                        </p>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                            <button type="button" onClick={() => setExcluir(null)} style={btn(false)}>Cancelar</button>
                            <button type="button" onClick={confirmarExcluir} style={btn(true, '#dc2626')}>Excluir</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Occurrences;
